/**
 * Seeds React Query caches from dashboard batch data so header hooks
 * can reuse subscription/notifications/assets without duplicate API calls.
 */

import type { QueryClient } from "@tanstack/react-query";
import type { DashboardData } from "@/services/dashboard/dashboardDataLoader";
import type { Profile } from "@/services/profileService";
import type { GroupedAssets } from "@/services/assetService";
import type { RawAssetData } from "@/hooks/assets/useOptimizedAssetData";
import {
  type SubscriptionStatusSnapshot,
  toSubscriptionStatusSnapshot,
} from "@/utils/subscriptionPlanState";
import {
  extractEmbeddedRealEstateLoans,
  mergeLiabilities,
  processLiabilitiesToDetailed,
} from "@/utils/liabilities/processLiabilities";
import { computeTotalLiabilityValue } from "@/utils/financialSummary";

/**
 * Map Grouped Assets To Raw Asset Data.
 */
export function mapGroupedAssetsToRawAssetData(assets: GroupedAssets): RawAssetData {
  return {
    generalAssets: assets.generalAssets || [],
    gold: assets.gold || [],
    insurance: assets.insurance || [],
    pf: assets.pf || [],
    ppf: assets.ppf || [],
    vpf: assets.vpf || [],
    stocks: assets.stocks || [],
    mutualFunds: assets.mutualFunds || [],
    bonds: assets.bonds || [],
    realEstate: assets.realEstate || [],
    crypto: assets.crypto || [],
    commodities: assets.commodities || [],
    fixedDeposits: assets.fixedDeposits || [],
    bankBalance: assets.bankBalance || [],
  };
}

/**
 * Seed Dashboard Derived Cache.
 */
export function seedDashboardDerivedCache(
  queryClient: QueryClient,
  userId: string,
  data: DashboardData
): void {
  queryClient.setQueryData<SubscriptionStatusSnapshot>(["subscription", userId], {
    isPaid: data.subscription.isPaid,
    tier: data.subscription.tier,
    planDisplayName: data.subscription.planDisplayName,
    endDate: data.subscription.endDate,
  });

  queryClient.setQueryData(["notifications-unread-count", userId], data.notificationsCount);

  if (data.assets) {
    queryClient.setQueryData(["assets", userId], mapGroupedAssetsToRawAssetData(data.assets));
  }

  queryClient.setQueryData(["profile", userId], data.profile ?? null);

  queryClient.setQueryData(["family", userId], {
    family: data.family,
    familyMembers: data.familyMembers,
    familyGoals: data.familyGoals,
    familyLicense: data.familyLicense,
  });

  if (data.transactions) {
    queryClient.setQueryData(["transactions", userId], data.transactions);
  }

  if (data.liabilities) {
    queryClient.setQueryData(["liabilities", userId], data.liabilities);
  }
}

export function computeDashboardTotalLiabilities(data: DashboardData): number {
  const explicit = processLiabilitiesToDetailed(data.liabilities ?? []);
  const embedded = extractEmbeddedRealEstateLoans(data.assets?.realEstate ?? []);
  return computeTotalLiabilityValue(mergeLiabilities(explicit, embedded));
}

/** True while the dashboard batch query is in-flight (avoid duplicate parallel API calls). */
export function isDashboardBatchFetching(
  queryClient: QueryClient,
  userId: string | undefined
): boolean {
  if (!userId) {
    return false;
  }
  return queryClient.getQueryState(["dashboard", userId])?.fetchStatus === "fetching";
}

/**
 * On /dashboard, header hooks should not fetch subscription/notifications until the
 * batch query finishes (success) or fails — prevents a race where batch completes
 * but derived caches are not seeded yet.
 */
export function shouldDeferToDashboardBatch(
  queryClient: QueryClient,
  userId: string | undefined,
  pathname: string = globalThis.window?.location.pathname ?? ""
): boolean {
  if (!userId || pathname !== "/dashboard") {
    return false;
  }

  const state = queryClient.getQueryState(["dashboard", userId]);
  if (!state || state.status === "pending") {
    return true;
  }
  if (state.fetchStatus === "fetching") {
    return true;
  }
  if (state.status === "success") {
    return true;
  }

  return false;
}

/** Profile from dashboard batch or seeded profile cache. */
export function getDashboardProfile(
  queryClient: QueryClient,
  userId: string | undefined
): Profile | null | undefined {
  if (!userId) {
    return undefined;
  }

  const cached = queryClient.getQueryData<Profile | null>(["profile", userId]);
  if (cached !== undefined) {
    return cached;
  }

  const dashboardData = queryClient.getQueryData<DashboardData>(["dashboard", userId]);
  if (dashboardData && Object.prototype.hasOwnProperty.call(dashboardData, "profile")) {
    return dashboardData.profile ?? null;
  }

  return undefined;
}

/**
 * Get Dashboard Unread Count.
 */
export function getDashboardUnreadCount(
  queryClient: QueryClient,
  userId: string | undefined
): number | undefined {
  if (!userId) {
    return undefined;
  }

  const seededCount = queryClient.getQueryData<number>(["notifications-unread-count", userId]);
  if (typeof seededCount === "number") {
    return seededCount;
  }

  const dashboardData = queryClient.getQueryData<DashboardData>(["dashboard", userId]);
  if (typeof dashboardData?.notificationsCount === "number") {
    return dashboardData.notificationsCount;
  }

  return undefined;
}

/**
 * Get Dashboard Subscription.
 */
export function getDashboardSubscription(
  queryClient: QueryClient,
  userId: string | undefined
): SubscriptionStatusSnapshot | undefined {
  if (!userId) {
    return undefined;
  }

  const cached = queryClient.getQueryData<Partial<SubscriptionStatusSnapshot>>([
    "subscription",
    userId,
  ]);
  if (cached) {
    return toSubscriptionStatusSnapshot(cached);
  }

  const dashboardData = queryClient.getQueryData<DashboardData>(["dashboard", userId]);
  if (dashboardData?.subscription) {
    return toSubscriptionStatusSnapshot(dashboardData.subscription);
  }

  return undefined;
}
